import { GameModeEnum } from 'lingo2-models';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  DestroyRef,
  EnvironmentInjector,
  inject,
  OnDestroy,
  runInInjectionContext,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GameService } from './services/game.service';
import { switchMap, tap } from 'rxjs';
import { ContentService } from './services/content.service';
import { AccountService } from './services/account.service';
import { IconsService } from './services/icons.service';
import { GameComponent } from './game/game.component';
import { NoDataComponent } from './no-data/no-data.component';
import { ScreenOptionsService } from './services/screen-options.service';

import { FrameAuthService } from './services/frame-auth.service';
import { HELLO_TIMEOUT_MS } from './services/frame-auth';
import { canBootstrapGame } from './services/board-game-command';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss',
})
export class AppComponent implements AfterViewInit, OnDestroy {
  @ViewChild('View_Game', { read: ViewContainerRef })
  view_game!: ViewContainerRef;
  @ViewChild('View_Error', { read: ViewContainerRef })
  view_error!: ViewContainerRef;

  public loaded = false;
  public lessonId: string = '';
  public userId: string = '';
  public lang: string = 'en';
  public svgSetIcon = IconsService.svgsetIconUrl;
  private activeGame: ComponentRef<GameComponent>;
  private errorGame: ComponentRef<NoDataComponent>;
  private initStarted = false;
  private gameMounted = false;
  private errorMounted = false;
  private helloTimer: ReturnType<typeof setTimeout> | null = null;
  private loadTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private accountService: AccountService,
    private contentService: ContentService,
    private icons: IconsService,
    private gameService: GameService,
    private screenOptionsService: ScreenOptionsService,
    private frameAuth: FrameAuthService,
    private cdr: ChangeDetectorRef
  ) {
    this.readQuery();
    this.frameAuth.start(this.gameService.host);
    this.bootstrapAccess();

    this.frameAuth.access$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onAccessAvailable());
    this.frameAuth.logout$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onLogout());
    this.frameAuth.deviceType$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((deviceType) => this.applyDeviceType(deviceType));

    if (this.frameAuth.isIframe) {
      this.helloTimer = setTimeout(() => this.onHelloTimeout(), HELLO_TIMEOUT_MS);
    } else {
      this.onTopWindowReady();
    }

    this.tryStartInit();
  }

  ngAfterViewInit(): void {
    this.loadGame();
  }

  ngOnDestroy(): void {
    if (this.helloTimer) {
      clearTimeout(this.helloTimer);
      this.helloTimer = null;
    }
    if (this.loadTimer) {
      clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
    this.unmountGame();
    this.unmountError();
  }

  public enoughtData(): boolean {
    const qp = new URLSearchParams(window.location.search);
    const host = this.gameService.host || qp.get('host') || '';
    const userId = this.gameService.userId || qp.get('userId') || '';
    const lessonId = this.lessonId || qp.get('lessonId') || '';
    return canBootstrapGame({
      host,
      userId,
      lessonId,
      token: this.gameService.token,
      sessionId: this.gameService.sessionId,
      mode: this.gameService.mode,
    });
  }

  private readQuery(): void {
    const qp = new URLSearchParams(window.location.search);
    this.lessonId = qp.get('lessonId') || '';
    this.gameService.host = qp.get('host') || '';
    this.gameService.mode =
      (qp.get('mode') as GameModeEnum) || GameModeEnum.single;
    this.gameService.userId = qp.get('userId') || '';
    this.gameService.gameId = qp.get('gameId') || '';
    this.gameService.sessionId = qp.get('sessionId') || '';
    const lang = qp.get('lang');
    if (lang) {
      this.lang = lang;
      this.gameService.locale = lang;
    }
    this.userId = this.gameService.userId;

    const deviceType = qp.get('deviceType');
    if (deviceType === 'mobile' || deviceType === 'desktop') {
      this.applyDeviceType(deviceType);
    }
  }

  private bootstrapAccess(): void {
    if (this.gameService.token) {
      return;
    }
    const cookie = this.frameAuth.readBootstrapCookie();
    if (cookie) {
      this.gameService.token = cookie;
    }
  }

  private onAccessAvailable(): void {
    this.clearHelloTimer();
    if (this.gameService.hasLocale) {
      this.lang = this.gameService.locale;
    }
    this.userId = this.gameService.userId;
    if (!this.initStarted && this.enoughtData()) {
      this.loaded = false;
      this.unmountError();
    }
    this.tryStartInit();
    this.loadGame();
  }

  private onTopWindowReady(): void {
    if (this.enoughtData()) {
      this.tryStartInit();
      return;
    }
    this.loaded = true;
  }

  private onHelloTimeout(): void {
    this.helloTimer = null;
    if (this.initStarted) {
      return;
    }
    if (this.gameService.token) {
      this.tryStartInit();
      return;
    }
    const cookie = this.frameAuth.readBootstrapCookie();
    if (cookie) {
      this.gameService.token = cookie;
      this.tryStartInit();
      return;
    }
    this.loaded = true;
    this.loadGame();
  }

  private tryStartInit(): void {
    if (this.initStarted || !this.enoughtData()) {
      return;
    }
    this.initStarted = true;
    this.clearHelloTimer();
    this.icons.init();
    // снимок сессии доски уже есть — не ждём HTTP урока/автора
    if (this.gameService.sessionId) {
      this.loaded = true;
      this.loadGame();
    }
    this.fetchUserAndLesson();
  }

  private fetchUserAndLesson(): void {
    if (this.gameService.userId && this.gameService.userId.length) {
      this.gameService
        .getUserById(this.gameService.userId)
        .pipe(
          tap((user) => {
            this.gameService.myUser.next(user);
            if (user && this.gameService.lesson) {
              this.loaded = true;
            }
          })
        )
        .subscribe();
    }

    if (this.lessonId) {
      this.contentService
        .getById(this.lessonId, false, ['author_id'])
        .pipe(
          tap((lesson) => {
            this.gameService.lesson = lesson;
          }),
          switchMap((res: any) => {
            return this.accountService.getUserById(res.author_id).pipe(
              tap((user: any) => {
                this.gameService.author.next(user);
              })
            );
          })
        )
        .subscribe({
          next: () => {
            this.loaded = true;
            this.loadGame();
          },
          error: () => {
            this.loaded = true;
            this.loadGame();
          },
        });
    }
  }

  private loadGame() {
    if (!this.view_game || !this.view_error) {
      return;
    }
    if (!this.loaded) {
      if (this.loadTimer) {
        clearTimeout(this.loadTimer);
      }
      this.loadTimer = setTimeout(() => this.loadGame(), 200);
      return;
    }
    if (this.gameMounted) {
      return;
    }
    if (!this.enoughtData()) {
      if (!this.errorMounted) {
        this.mountError();
      }
      return;
    }
    this.unmountError();
    this.mountGame();
  }

  private mountGame(): void {
    if (!this.view_game || this.gameMounted) {
      return;
    }
    try {
      this.activeGame = runInInjectionContext(this.environmentInjector, () => {
        return this.view_game.createComponent(GameComponent, {
          environmentInjector: this.environmentInjector,
        });
      });
      this.activeGame.instance.lessonId = this.lessonId;
      this.gameMounted = true;
      this.cdr.detectChanges();
    } catch {
      // Ignore
    }
  }

  private mountError(): void {
    if (!this.view_error || this.errorMounted) {
      return;
    }
    this.errorGame = runInInjectionContext(this.environmentInjector, () => {
      return this.view_error.createComponent(NoDataComponent, {
        environmentInjector: this.environmentInjector,
      });
    });
    this.errorMounted = true;
  }

  private unmountGame(): void {
    try {
      this.activeGame?.destroy();
      this.view_game?.clear();
    } catch {
      // Ignore
    }
    this.gameMounted = false;
    this.activeGame = undefined as unknown as ComponentRef<GameComponent>;
  }

  private unmountError(): void {
    try {
      this.errorGame?.destroy();
      this.view_error?.clear();
    } catch {
      // Ignore
    }
    this.errorMounted = false;
    this.errorGame = undefined as unknown as ComponentRef<NoDataComponent>;
  }

  private onLogout(): void {
    this.clearHelloTimer();
    this.initStarted = false;
    this.loaded = true;
    this.unmountGame();
    this.mountError();
    this.cdr.detectChanges();
  }

  private applyDeviceType(deviceType: 'mobile' | 'desktop'): void {
    this.screenOptionsService.setDeviceType(deviceType);
    if (deviceType === 'mobile') {
      document.body.classList.add('mobile');
    } else {
      document.body.classList.remove('mobile');
    }
  }

  private clearHelloTimer(): void {
    if (this.helloTimer) {
      clearTimeout(this.helloTimer);
      this.helloTimer = null;
    }
  }
}
